import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  ArrowRight,
  Database,
  RefreshCw,
  Shield,
  Trophy,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * /about — operating thesis, not a manifesto. One promise, three
 * ideas, one CTA. Editorial layout, no marketing copy.
 */
export const AboutPage = () => {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('landing.seo.aboutTitle', { defaultValue: 'GymGamify — About' });
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        t('landing.seo.aboutDescription', {
          defaultValue:
            'GymGamify is the operating system for boutique studios, big-box chains, and independent trainers. Built by operators. Free forever, for every gym.',
        })
      );
    }
  }, [t]);

  return (
    <div className="layout-shell py-14 lg:py-20">
      <header className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="motion-enter">
          <span className="hero-kicker">
            {t('landingAboutPage.kicker', { defaultValue: 'About GymGamify' })}
          </span>
          <h1 className="mt-6 editorial-title text-[var(--color-text)]">
            {t('landingAboutPage.title', {
              defaultValue: 'Built by operators, for operators.',
            })}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-[var(--color-text-muted)]">
            {t('landingAbout.body', {
              defaultValue:
                'A small team of gym operators, trainers, and software engineers building the gym operating system we always wanted — and shipping it free, for every gym, on day one.',
            })}
          </p>
        </div>

        <div className="editorial-panel p-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-200">
            Operating thesis
          </p>
          <p className="mt-4 font-display text-3xl uppercase leading-[1.05] text-[var(--color-text)] md:text-4xl">
            {t('landingAboutPage.thesisTitle', {
              defaultValue: 'Train. Retain. Reward. Measure the loop.',
            })}
          </p>
          <p className="mt-4 text-sm leading-7 text-[var(--color-text-muted)]">
            {t('landingAboutPage.thesisBody', {
              defaultValue:
                'Retention is a flywheel: the right streak, the right reward, the right coach check-in, the right reminder. We wire those surfaces so the loop actually runs, not just the dashboard.',
            })}
          </p>
        </div>
      </header>

      {/* Three values. One card per value, cardless but framed by
          the editorial-panel treatment. */}
      <section className="mt-14">
        <ol className="space-y-5">
          <li className="editorial-panel motion-hover-lift p-8 md:p-10">
            <div className="grid gap-6 md:grid-cols-[auto_1fr] md:items-start">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-primary-200/15 bg-primary-200/10">
                <Activity className="h-5 w-5 text-primary-100" />
              </span>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
                  Value 01
                </p>
                <h2 className="mt-2 font-display text-3xl uppercase tracking-[0.04em] text-[var(--color-text)]">
                  Operational, not aspirational
                </h2>
                <p className="mt-3 max-w-3xl text-base leading-8 text-[var(--color-text-muted)]">
                  We design for the day-to-day reality of gym owners: how
                  sessions actually get logged, how trainers actually
                  follow up, how renewals actually get decided. The
                  product wins on Tuesday at 6 p.m., not on a sales call.
                </p>
              </div>
            </div>
          </li>

          <li className="editorial-panel motion-hover-lift p-8 md:p-10">
            <div className="grid gap-6 md:grid-cols-[auto_1fr] md:items-start">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-primary-200/15 bg-primary-200/10">
                <Trophy className="h-5 w-5 text-primary-100" />
              </span>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
                  Value 02
                </p>
                <h2 className="mt-2 font-display text-3xl uppercase tracking-[0.04em] text-[var(--color-text)]">
                  Momentum over mechanics
                </h2>
                <p className="mt-3 max-w-3xl text-base leading-8 text-[var(--color-text-muted)]">
                  Members stay for the streak they can see, not the
                  spreadsheet they ignore. We optimize for visible
                  momentum — the leaderboard line, the badge tile, the
                  confirmation banner — not for completeness.
                </p>
              </div>
            </div>
          </li>

          <li className="editorial-panel motion-hover-lift p-8 md:p-10">
            <div className="grid gap-6 md:grid-cols-[auto_1fr] md:items-start">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-primary-200/15 bg-primary-200/10">
                <Shield className="h-5 w-5 text-primary-100" />
              </span>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
                  Value 03
                </p>
                <h2 className="mt-2 font-display text-3xl uppercase tracking-[0.04em] text-[var(--color-text)]">
                  Your data, your call
                </h2>
                <p className="mt-3 max-w-3xl text-base leading-8 text-[var(--color-text-muted)]">
                  Every gym owns its member data, can export it in
                  standard formats, and can self-host on its own
                  infrastructure if the platform ever stops fitting. The
                  ledger is open and verifiable — that is the trust
                  contract.
                </p>
              </div>
            </div>
          </li>
        </ol>
      </section>

      {/* The loop — visualize the operating thesis. */}
      <section className="mt-16">
        <div className="editorial-panel p-8 md:p-12">
          <div className="grid gap-10 md:grid-cols-[1.1fr_0.9fr] md:items-center">
            <div>
              <span className="hero-kicker">How the loop runs</span>
              <h2 className="mt-5 editorial-title text-[var(--color-text)]">
                The same loop, every week.
              </h2>
              <p className="mt-5 max-w-xl text-base leading-8 text-[var(--color-text-muted)]">
                Train. Retain. Reward. Measure. Every product surface
                maps to one of those four verbs, and the ledger ties
                them together. If a feature does not feed the loop, we
                cut it.
              </p>
            </div>
            <LoopDiagram />
          </div>
        </div>
      </section>

      {/* How we run — three things we do, three things we do not. */}
      <section className="mt-16">
        <div className="grid gap-5 md:grid-cols-2">
          <div className="editorial-panel p-8 md:p-10">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-200">
              What we do
            </p>
            <ul className="mt-5 space-y-3 text-sm text-[var(--color-text-muted)]">
              {[
                'Ship the full product free, on day one, with no tiered paywall.',
                'Run a tamper-proof ledger every member can verify themselves.',
                'Keep the codebase exportable and self-hostable.',
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <RefreshCw className="mt-0.5 h-4 w-4 shrink-0 text-primary-200" />
                  <span className="leading-6">{line}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="editorial-panel p-8 md:p-10">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
              What we do not do
            </p>
            <ul className="mt-5 space-y-3 text-sm text-[var(--color-text-muted)]">
              {[
                'Hide features behind a "Pro" or "Enterprise" tier.',
                'Resell member data, ever. The data export and the self-host path are the proof.',
                'Pretend gamification is a magic trick. The loop runs because the trainer and the member show up.',
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <Database className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-text-muted)]" />
                  <span className="leading-6">{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Final CTA — same voice as the rest of the marketing surface. */}
      <section className="mt-16">
        <div className="flex flex-col items-start gap-6 border-t border-[var(--color-border)] pt-12 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)]">
              See it
            </p>
            <p className="mt-3 max-w-xl font-display text-3xl uppercase leading-tight text-[var(--color-text)]">
              The product is the argument. Open an account, log a session.
            </p>
          </div>
          <Link to="/register" className="btn btn-primary whitespace-nowrap">
            Start your gym
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

// Loop diagram — four stations in a row, arrows between them.
// The fourth arrow loops back to the first, encoded as a wide
// gradient line on the bottom edge.
const LoopDiagram = () => {
  const steps = [
    { no: '01', head: 'Train', body: 'A session gets logged. Set goes in. Volume rolls up.' },
    { no: '02', head: 'Retain', body: 'Streak ticks. The member sees the calendar light up.' },
    { no: '03', head: 'Reward', body: 'Points, a badge, a leaderboard line, a reward redeem.' },
    { no: '04', head: 'Measure', body: 'The ledger entry. Coach reads it, member sees the next target.' },
  ];
  return (
    <div className="relative">
      <ol className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {steps.map((s) => (
          <li
            key={s.no}
            className="rounded-2xl border border-[var(--color-border)] bg-white/[0.03] p-4"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-primary-200">
              {s.no}
            </p>
            <p className="mt-2 font-display text-lg uppercase tracking-[0.04em] text-[var(--color-text)]">
              {s.head}
            </p>
            <p className="mt-2 text-xs leading-6 text-[var(--color-text-muted)]">
              {s.body}
            </p>
          </li>
        ))}
      </ol>
      <div className="mt-4 flex items-center gap-3 text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
        <span className="font-mono text-primary-200">loop</span>
        <span className="h-px flex-1 bg-gradient-to-r from-primary-300/60 via-primary-300/10 to-transparent" />
        <span className="font-mono">back to 01</span>
      </div>
    </div>
  );
};

export default AboutPage;
